// ════════════════════════════════════════════════════════════════════════════
//  features/renave/ReceberTransferencia.tsx — DESTINO de transferências (Fatia 4).
//
//  • Receber de filial: lista o estoque das filiais (mesmo CNPJ raiz, mesmo
//    banco) e pede a transferência entre filiais — o veículo é criado nesta
//    empresa e a filial de origem fica com ele "Transferido".
//  • Transferências recebidas: autorizações que outras revendas deram para o
//    CNPJ desta empresa; "Cadastrar" abre a nova entrada já preenchida.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  renaveApi, cpfOperadorSalvo, cpfValido, descricaoVeiculo, formatarCnpj, formatarMoeda, salvarCpfOperador,
  STATUS_OPERACAO, type AutorizacaoRecebida, type VeiculoFilial,
} from './api';

function agoraLocal() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

// ── Receber de filial ───────────────────────────────────────────────────────

export function ReceberFilialDialog({ aberto, onClose }: { aberto: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['renave-filiais'], queryFn: renaveApi.veiculosDeFiliais, enabled: aberto });

  const [escolhido, setEscolhido] = useState<VeiculoFilial | null>(null);
  const [km, setKm] = useState('');
  const [medicao, setMedicao] = useState(agoraLocal);
  const [data, setData] = useState(() => agoraLocal().slice(0, 10));
  const [valor, setValor] = useState('');
  const [cpf, setCpf] = useState(() => cpfOperadorSalvo(user?.id));
  const [enviando, setEnviando] = useState(false);

  const escolher = (f: VeiculoFilial) => {
    setEscolhido(f);
    setKm(f.quilometragem?.toString() ?? '');
    setValor(f.valorCompra?.toString() ?? '');
    setMedicao(agoraLocal());
  };

  const fechar = () => { setEscolhido(null); onClose(); };

  const receber = async () => {
    if (!escolhido) return;
    if (!km.trim()) { toast.error('Informe a quilometragem atual.'); return; }
    if (!cpfValido(cpf)) { toast.error('Informe um CPF válido do operador responsável (exigido pelo RENAVE).'); return; }
    salvarCpfOperador(user?.id, cpf);
    setEnviando(true);
    try {
      const r = await renaveApi.receberDeFilial(escolhido.veiculoId, {
        quilometragem: Number(km), dataHoraMedicao: `${medicao}:00`, dataTransferencia: data,
        valor: valor.trim() ? Number(valor.replace(',', '.')) : null, cpfOperador: cpf.replace(/\D/g, ''),
      });
      const res = r.resultado;
      if (res.status === STATUS_OPERACAO.Concluida) toast.success(res.mensagem);
      else if (res.status === STATUS_OPERACAO.Incerta) toast.warning(res.mensagem, { duration: 12_000 });
      else toast.error(res.mensagem + (res.logIdRastreabilidade ? ` (protocolo ${res.logIdRastreabilidade})` : ''), { duration: 12_000 });
      fechar();
      navigate(`/estoque/veiculos/${r.veiculoId}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={o => { if (!o) fechar(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receber veículo de filial</DialogTitle>
          <DialogDescription>
            Filiais com o mesmo CNPJ raiz e o RENAVE ativo. A transferência é registrada por esta empresa (destino); o veículo
            entra aqui já confirmado e a filial de origem fica com ele como transferido, aguardando a NF-e de saída.
          </DialogDescription>
        </DialogHeader>

        {!escolhido ? (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filial</TableHead>
                  <TableHead>Veículo</TableHead>
                  <TableHead>Placa</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.isLoading ? (
                  <TableRow><TableCell colSpan={5} className="text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
                ) : q.error ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-destructive">{getApiErrorMessage(q.error)}</TableCell></TableRow>
                ) : !q.data?.length ? (
                  <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                    Nenhum veículo em estoque nas filiais (mesmo CNPJ raiz, com RENAVE ativo).
                  </TableCell></TableRow>
                ) : q.data.map(f => (
                  <TableRow key={f.veiculoId}>
                    <TableCell className="text-xs">
                      <div className="font-medium">{f.nomeEmpresa ?? '—'}</div>
                      <div className="font-mono text-muted-foreground">{formatarCnpj(f.cnpjEmpresa)}</div>
                    </TableCell>
                    <TableCell>{descricaoVeiculo({ marca: f.marca, modelo: f.modelo, anoFabricacao: null, anoModelo: f.anoModelo })}</TableCell>
                    <TableCell className="font-mono">{f.placa ?? '—'}</TableCell>
                    <TableCell className="max-w-56 text-xs">
                      {f.impedimento ? <span className="text-amber-700">Falta: {f.impedimento}</span> : (f.renaveEstado ?? '—')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" disabled={!!f.impedimento} onClick={() => escolher(f)}>Receber</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">
              <strong>{descricaoVeiculo({ marca: escolhido.marca, modelo: escolhido.modelo, anoFabricacao: null, anoModelo: escolhido.anoModelo })}</strong>
              {' '}· {escolhido.placa} · de {escolhido.nomeEmpresa} ({formatarCnpj(escolhido.cnpjEmpresa)})
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Rotulo texto="Quilometragem atual *">
                <Input value={km} inputMode="numeric" onChange={e => setKm(e.target.value.replace(/\D/g, ''))} />
              </Rotulo>
              <Rotulo texto="Leitura do hodômetro em">
                <Input type="datetime-local" value={medicao} onChange={e => setMedicao(e.target.value)} />
              </Rotulo>
              <Rotulo texto="Data da transferência">
                <Input type="date" value={data} onChange={e => setData(e.target.value)} />
              </Rotulo>
              <Rotulo texto="Valor da transferência (R$)" ajuda={`Custo na filial: ${formatarMoeda(escolhido.valorCompra)}. Vira o custo aqui (margem do usado).`}>
                <Input value={valor} inputMode="decimal" onChange={e => setValor(e.target.value)} />
              </Rotulo>
              <Rotulo texto="CPF do operador responsável">
                <Input value={cpf} inputMode="numeric" maxLength={14} onChange={e => setCpf(e.target.value)}
                  className={cpf && !cpfValido(cpf) ? 'border-destructive' : ''} />
              </Rotulo>
            </div>
          </div>
        )}

        <DialogFooter>
          {escolhido && <Button variant="outline" onClick={() => setEscolhido(null)} disabled={enviando}>Voltar</Button>}
          {escolhido
            ? <Button onClick={receber} disabled={enviando}>
                {enviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Truck className="mr-2 h-4 w-4" />}Solicitar transferência
              </Button>
            : <Button variant="outline" onClick={fechar}>Fechar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Autorizações recebidas de outras revendas ───────────────────────────────

export function AutorizacoesRecebidasDialog({ aberto, onClose, onCadastrar }: {
  aberto: boolean;
  onClose: () => void;
  /** Abre a nova entrada já preenchida (origem = outra revenda). */
  onCadastrar: (a: AutorizacaoRecebida) => void;
}) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['renave-autorizacoes-recebidas'], queryFn: renaveApi.autorizacoesRecebidas, enabled: aberto });

  return (
    <Dialog open={aberto} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transferências autorizadas para esta empresa</DialogTitle>
          <DialogDescription>
            Veículos que outras revendas autorizaram no RENAVE para o CNPJ desta empresa. Cadastre o recebimento e, na tela do
            veículo, solicite a transferência.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Placa</TableHead>
                <TableHead>Chassi</TableHead>
                <TableHead>Revenda de origem</TableHead>
                <TableHead>Autorizada em</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
              ) : q.error ? (
                <TableRow><TableCell colSpan={5} className="text-center text-destructive">{getApiErrorMessage(q.error)}</TableCell></TableRow>
              ) : !q.data?.length ? (
                <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">Nenhuma autorização em aberto.</TableCell></TableRow>
              ) : q.data.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{a.placa ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{a.chassi ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    <div>{a.nomeOrigem ?? '—'}</div>
                    <div className="font-mono text-muted-foreground">{formatarCnpj(a.cnpjOrigem)}</div>
                  </TableCell>
                  <TableCell className="text-xs">{a.dataHoraAutorizacao ? new Date(a.dataHoraAutorizacao).toLocaleString('pt-BR') : '—'}</TableCell>
                  <TableCell className="text-right">
                    {a.veiculoCadastradoId
                      ? <Button size="sm" variant="outline" onClick={() => { onClose(); navigate(`/estoque/veiculos/${a.veiculoCadastradoId}`); }}>Abrir</Button>
                      : <Button size="sm" onClick={() => onCadastrar(a)}>Cadastrar</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Fechar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Rotulo({ texto, ajuda, children }: { texto: string; ajuda?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block space-y-1.5">
        <span className="text-xs font-medium leading-none">{texto}</span>
        {children}
      </label>
      {ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>}
    </div>
  );
}
